"use client";

import { useState, useTransition } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import {
  createRegistration,
  initiateRegistrationPayment,
  confirmPayment,
} from "@/lib/actions/registrations";
import type { FormField } from "@/lib/types/form-fields";
import {
  type PaymentMode,
  type SlidingTier,
  type ChargeCurrency,
  type DisplayCurrency,
  isPaidMode,
  choiceRequiresPayment,
  formatMoney,
} from "@/lib/types/payment";
import {
  AmountSelector,
  type AmountChoice,
} from "@/components/public/amount-selector";
import { openPaystack } from "@/lib/paystack/inline";

type Props = {
  slug: string;
  fields: FormField[];
  payment: {
    mode: PaymentMode;
    price_cents: number | null;
    suggested_cents: number | null;
    min_cents: number | null;
    sliding_tiers: SlidingTier[] | null;
    /** Charge currency Paystack settles in (what *_cents are stored in). */
    currency: string;
    /** Optional presentation currency shown to the buyer (e.g. EUR over XOF). */
    display_currency: string | null;
  };
};

type ServerError =
  | { kind: "general"; message: string }
  | { kind: "field"; fieldId: string; message: string }
  | { kind: "already"; registrationId: string };

export function RegistrationForm(props: Props) {
  const [open, setOpen] = useState(false);

  return open ? (
    <RegistrationFormBody {...props} />
  ) : (
    <Button
      type="button"
      size="lg"
      className="rounded-full px-8"
      onClick={() => setOpen(true)}
    >
      Reserve your place
    </Button>
  );
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * How long to keep asking Paystack after the popup closes, before telling the
 * payer we haven't seen anything. Roughly 30s in total — long enough for a
 * SnapScan tap to land, short enough not to strand someone who simply changed
 * their mind. Spaced out rather than tight so we stay well inside Paystack's
 * rate limit on /transaction/verify.
 */
const CLOSE_POLL_DELAYS_MS = [2000, 3000, 5000, 5000, 5000, 5000, 5000];

function RegistrationFormBody({ slug, fields, payment }: Props) {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<ServerError | null>(null);
  const [success, setSuccess] = useState<{ message: string } | null>(null);
  // Fixed price has no chooser — the amount is the offering's price, so seed
  // it up front. (Other paid modes start null and are set by AmountSelector.)
  // price_cents is already in charge cents, exactly what the server validates.
  const [amount, setAmount] = useState<AmountChoice>(() =>
    payment.mode === "fixed" && payment.price_cents != null
      ? { amount_cents: payment.price_cents }
      : { amount_cents: null }
  );
  const [donationNote, setDonationNote] = useState("");
  // Set while we wait to see whether an out-of-band payment (SnapScan, EFT)
  // lands after the Paystack popup closed.
  const [checking, setChecking] = useState(false);

  const phoneField = fields.find((f) => f.type === "phone");
  const visibleFields = fields.filter((f) => f.type !== "phone");
  // Charge currency = what Paystack settles in; display = what the buyer sees.
  const chargeCurrency = (payment.currency || "ZAR") as ChargeCurrency;
  const displayCurrency = (payment.display_currency ||
    chargeCurrency) as DisplayCurrency;
  const paid = isPaidMode(payment.mode);
  // Whether this particular choice needs Paystack. A donation left at zero
  // and a sliding-scale tier priced at zero both skip payment entirely.
  const requiresPayment = choiceRequiresPayment(
    payment.mode,
    amount.amount_cents
  );
  const isFreeTier =
    payment.mode === "sliding_scale" && amount.amount_cents === 0;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const fd = new FormData(e.currentTarget);

    const attendee_name = String(fd.get("attendee_name") ?? "").trim();
    const attendee_email = String(fd.get("attendee_email") ?? "").trim();
    const attendee_phone = phoneField
      ? String(fd.get("attendee_phone") ?? "").trim() || null
      : null;

    // Build answers map by walking visible fields
    const answers: Record<string, unknown> = {};
    for (const f of visibleFields) {
      switch (f.type) {
        case "divider":
          break;
        case "consent":
          answers[f.id] = fd.get(`f_${f.id}`) === "on";
          break;
        case "multiselect":
          answers[f.id] = fd.getAll(`f_${f.id}`).map(String);
          break;
        case "number": {
          const raw = String(fd.get(`f_${f.id}`) ?? "");
          answers[f.id] = raw === "" ? null : Number(raw);
          break;
        }
        default:
          answers[f.id] = String(fd.get(`f_${f.id}`) ?? "");
      }
    }

    const trimmedNote = donationNote.trim();
    const noteForPayload =
      payment.mode === "donation" && trimmedNote.length > 0 ? trimmedNote : null;

    if (!requiresPayment) {
      startTransition(async () => {
        const result = await createRegistration(
          slug,
          {
            attendee_name,
            attendee_email,
            attendee_phone,
            answers,
            donation_note: noteForPayload,
          },
          // The server re-validates a free sliding tier against the offering's
          // configured tiers; free and zero-donation flows ignore this.
          isFreeTier ? 0 : null
        );
        handleResult(result);
      });
      return;
    }

    // Paid path: initiate → Paystack popup → confirm
    if (amount.amount_cents == null) {
      setError({ kind: "general", message: "Choose an amount to continue" });
      return;
    }

    startTransition(async () => {
      const init = await initiateRegistrationPayment(
        slug,
        {
          attendee_name,
          attendee_email,
          attendee_phone,
          answers,
          donation_note: noteForPayload,
        },
        amount.amount_cents
      );
      if (!init.ok) {
        handleResult(init);
        return;
      }

      try {
        const outcome = await openPaystack({
          key: init.publicKey,
          email: init.email,
          amount: init.amount_cents,
          currency: chargeCurrency,
          ref: init.reference,
          label: init.label,
          metadata: {
            registration_id: init.registrationId,
            slug,
            custom_fields: init.customFields,
          },
        });
        if (outcome.kind === "cancelled") {
          // The popup closing does NOT mean the payment failed. SnapScan, EFT
          // and the other QR channels complete *out of band*: the payer leaves
          // for another app, pays there, and Paystack tells us over the
          // charge.success webhook. The inline callback can never fire for
          // those, so `onClose` is the normal path for a successful QR payment
          // — and asserting "cancelled" here told people who had genuinely just
          // paid that their payment had failed. Some then paid a second time.
          //
          // So ask Paystack before saying anything. The money may still be in
          // flight when the popup closes, hence the poll rather than one check.
          setChecking(true);
          try {
            for (const delay of CLOSE_POLL_DELAYS_MS) {
              await sleep(delay);
              const settled = await confirmPayment(init.reference);
              if (settled.ok) {
                window.location.href = settled.redirectUrl;
                return;
              }
            }
          } finally {
            setChecking(false);
          }
          setError({
            kind: "general",
            message:
              "We haven't seen this payment come through yet. If you paid in another app, it can take a few minutes — check your email, and don't pay again until you've looked. If you closed the payment window, you can try again below.",
          });
          return;
        }
        const confirmed = await confirmPayment(outcome.reference);
        if (!confirmed.ok) {
          setError({ kind: "general", message: confirmed.error });
          return;
        }
        window.location.href = confirmed.redirectUrl;
      } catch (err) {
        setError({
          kind: "general",
          message:
            err instanceof Error
              ? err.message
              : "Something went wrong with Paystack",
        });
      }
    });
  };

  function handleResult(
    result:
      | Awaited<ReturnType<typeof createRegistration>>
      | Awaited<ReturnType<typeof initiateRegistrationPayment>>
  ) {
    if (result.ok && "redirectUrl" in result) {
      window.location.href = result.redirectUrl;
      return;
    }
    if ("alreadyRegistered" in result && result.alreadyRegistered) {
      setSuccess({
        message:
          "You're already registered. We've resent your confirmation — check your inbox.",
      });
      return;
    }
    if ("fieldId" in result) {
      setError({
        kind: "field",
        fieldId: result.fieldId,
        message: result.message,
      });
      return;
    }
    setError({
      kind: "general",
      message: ("error" in result && result.error) || "Something went wrong",
    });
  }

  if (success) {
    return (
      <div className="rounded-2xl border bg-card p-6">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 size-5 text-[--color-sage]" />
          <div>
            <h3 className="font-serif text-xl">Already registered</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {success.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const fieldErrorFor = (id: string) =>
    error?.kind === "field" && error.fieldId === id ? error.message : null;

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border bg-card p-6 space-y-5"
    >
      <h3 className="font-serif text-xl">Reserve your place</h3>

      <FieldRow label="Name" required>
        <Input
          name="attendee_name"
          autoComplete="name"
          required
          disabled={pending}
        />
      </FieldRow>
      <FieldRow label="Email" required>
        <Input
          name="attendee_email"
          type="email"
          autoComplete="email"
          required
          disabled={pending}
        />
      </FieldRow>
      {phoneField && (
        <FieldRow
          label={phoneField.label}
          required={phoneField.required}
          error={fieldErrorFor(phoneField.id)}
        >
          <Input
            name="attendee_phone"
            type="tel"
            autoComplete="tel"
            placeholder="+27 …"
            required={phoneField.required}
            disabled={pending}
          />
        </FieldRow>
      )}

      {visibleFields.map((f) => (
        <DynamicField
          key={f.id}
          field={f}
          disabled={pending}
          error={fieldErrorFor(f.id)}
        />
      ))}

      {paid && (
        <AmountSelector
          mode={payment.mode}
          price_cents={payment.price_cents}
          suggested_cents={payment.suggested_cents}
          min_cents={payment.min_cents}
          sliding_tiers={payment.sliding_tiers}
          currency={chargeCurrency}
          displayCurrency={displayCurrency}
          value={amount}
          onChange={setAmount}
        />
      )}

      {payment.mode === "donation" &&
        amount.amount_cents != null &&
        amount.amount_cents > 0 && (
          <FieldRow
            label="Note to host (optional)"
            hint="Visible to the host alongside your donation."
          >
            <Textarea
              value={donationNote}
              onChange={(e) => setDonationNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Anything you'd like the host to know — a dedication, intention, or a quick hello."
              disabled={pending}
            />
          </FieldRow>
        )}

      {checking && (
        <p className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin" />
          <span>
            Checking with Paystack — if you paid with SnapScan or EFT this can
            take a moment. Please don&apos;t pay again.
          </span>
        </p>
      )}

      {error?.kind === "general" && !checking && (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error.message}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full rounded-full"
        disabled={pending}
      >
        {pending ? (
          <>
            <Loader2 className="size-4 animate-spin" />{" "}
            {checking
              ? "Checking your payment…"
              : requiresPayment
                ? "Opening payment…"
                : "Confirming…"}
          </>
        ) : requiresPayment ? (
          payment.mode === "donation" && amount.amount_cents
            ? `Continue to registration and donation · ${formatMoney(amount.amount_cents, chargeCurrency, displayCurrency)}`
            : "Continue to payment"
        ) : payment.mode === "donation" ? (
          "Complete registration"
        ) : (
          "Confirm registration"
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        By registering you agree to receive emails about this offering and to
        Offerings&apos;{" "}
        <a
          href="/terms"
          target="_blank"
          rel="noreferrer"
          className="underline-offset-4 hover:text-foreground hover:underline"
        >
          terms
        </a>{" "}
        and{" "}
        <a
          href="/privacy"
          target="_blank"
          rel="noreferrer"
          className="underline-offset-4 hover:text-foreground hover:underline"
        >
          privacy policy
        </a>
        .
      </p>
    </form>
  );
}

function DynamicField({
  field,
  disabled,
  error,
}: {
  field: FormField;
  disabled: boolean;
  error: string | null;
}) {
  const name = `f_${field.id}`;

  if (field.type === "divider") {
    return field.label ? (
      <div className="border-t pt-4">
        <p className="whitespace-pre-wrap text-sm text-muted-foreground">
          {field.label}
        </p>
      </div>
    ) : (
      <div className="border-t" />
    );
  }

  if (field.type === "consent") {
    return (
      <div className="space-y-1.5">
        <label className="flex cursor-pointer items-start gap-3 text-sm">
          <Checkbox
            name={name}
            required={field.required}
            disabled={disabled}
            className="mt-0.5"
          />
          <span className="text-muted-foreground">
            {field.label}
            {field.required && <span className="text-destructive"> *</span>}
          </span>
        </label>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  if (field.type === "long_text") {
    return (
      <FieldRow label={field.label} required={field.required} error={error}>
        <Textarea
          name={name}
          rows={3}
          placeholder={field.placeholder ?? ""}
          required={field.required}
          disabled={disabled}
        />
      </FieldRow>
    );
  }

  if (field.type === "number") {
    return (
      <FieldRow label={field.label} required={field.required} error={error}>
        <Input
          name={name}
          type="number"
          min={field.min}
          max={field.max}
          required={field.required}
          disabled={disabled}
        />
      </FieldRow>
    );
  }

  if (field.type === "select") {
    return (
      <FieldRow label={field.label} required={field.required} error={error}>
        <Select name={name} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>
    );
  }

  if (field.type === "multiselect") {
    return (
      <div className="space-y-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-destructive"> *</span>}
        </Label>
        <div className="space-y-2">
          {field.options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 text-sm"
            >
              <Checkbox name={name} value={o.value} disabled={disabled} />{" "}
              {o.label}
            </label>
          ))}
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  if (field.type === "file") {
    // Phase 1: URL-only fallback. Phase 4 swaps for real upload.
    return (
      <FieldRow
        label={field.label}
        required={field.required}
        error={error}
        hint="Paste a public link (Drive, Dropbox, etc.). Real uploads coming soon."
      >
        <Input
          name={name}
          type="url"
          placeholder="https://…"
          required={field.required}
          disabled={disabled}
        />
      </FieldRow>
    );
  }

  return null;
}

function FieldRow({
  label,
  required,
  error,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string | null;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
